import exifr from 'exifr';
import type { ExifData } from '../types';

/**
 * Extract EXIF data from an image file
 */
export async function extractExifData(file: File): Promise<ExifData> {
  const defaultData: ExifData = {
    takenAt: null,
    cameraMake: null,
    cameraModel: null,
    lat: null,
    lng: null,
    gpsAccuracy: null,
  };

  try {
    // Parse EXIF data
    const exif = await exifr.parse(file, {
      // Specify which tags we want
      pick: [
        'DateTimeOriginal',
        'CreateDate',
        'Make',
        'Model',
        'GPSLatitude',
        'GPSLongitude',
        'GPSLatitudeRef',
        'GPSLongitudeRef',
        'GPSHPositioningError',
        'GPSAltitude',
      ],
      // Enable GPS parsing
      gps: true,
    });

    if (!exif) {
      return defaultData;
    }

    // Extract GPS coordinates
    let lat: number | null = null;
    let lng: number | null = null;

    // exifr with gps: true returns latitude/longitude directly
    if (typeof exif.latitude === 'number' && typeof exif.longitude === 'number') {
      lat = exif.latitude;
      lng = exif.longitude;
    } 
    // Fallback: manually parse if exifr didn't convert
    else if (exif.GPSLatitude && exif.GPSLongitude) {
      lat = exif.GPSLatitude;
      lng = exif.GPSLongitude;

      // Apply reference direction
      if (exif.GPSLatitudeRef === 'S' && lat !== null) {
        lat = -Math.abs(lat);
      }
      if (exif.GPSLongitudeRef === 'W' && lng !== null) {
        lng = -Math.abs(lng);
      }
    }

    // Safety check: apply correct sign based on reference direction
    if (lat !== null && lng !== null) {
      if (exif.GPSLongitudeRef === 'W' && lng > 0) {
        lng = -lng;
      }
      if (exif.GPSLatitudeRef === 'S' && lat > 0) {
        lat = -lat;
      }
    }

    // Parse date
    let takenAt: Date | null = null;
    if (exif.DateTimeOriginal) {
      takenAt = new Date(exif.DateTimeOriginal);
    } else if (exif.CreateDate) {
      takenAt = new Date(exif.CreateDate);
    }

    return {
      takenAt,
      cameraMake: exif.Make || null,
      cameraModel: exif.Model || null,
      lat,
      lng,
      gpsAccuracy: exif.GPSHPositioningError || null,
    };
  } catch (error) {
    console.error('Error extracting EXIF data:', error);
    return defaultData;
  }
}

/**
 * Check if an image file likely has EXIF data based on type
 */
export function canHaveExif(file: File): boolean {
  const exifTypes = ['image/jpeg', 'image/heic', 'image/heif', 'image/tiff'];
  return exifTypes.includes(file.type);
}

/**
 * Create a preview URL for an image file
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke a preview URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Generate a unique ID for a photo file
 */
export function generatePhotoId(): string {
  return `photo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}